import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  ACTION_TYPE_BADGE,
  ACTION_TYPE_LABELS,
} from "./marketingActionTypeLabels";

export interface MarketingActionDto {
  id?: number;
  title?: string;
  detail?: string;
  actionType?: string;
  dateFrom?: string | Date;
  dateTo?: string | Date;
  associatedProducts?: string[];
  folderLinks?: Array<{
    path?: string;
    label?: string;
    folderType?: string;
  }>;
  outlookSyncStatus?: string;
}

const formatDate = (d: string | Date | null | undefined) => {
  if (!d) return "—";
  const date = typeof d === "string" ? new Date(d) : d;
  return date.toLocaleDateString("cs-CZ");
};

interface MarketingActionGridProps {
  actions: MarketingActionDto[];
  totalPages: number;
  pageNumber: number;
  onPageChange: (page: number) => void;
  onActionClick: (id: number) => void;
  isLoading?: boolean;
}

const MarketingActionGrid: React.FC<MarketingActionGridProps> = ({
  actions,
  totalPages,
  pageNumber,
  onPageChange,
  onActionClick,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <div className="p-8 text-center text-gray-500 dark:text-graphite-muted text-sm">Načítání...</div>
    );
  }

  if (actions.length === 0) {
    return (
      <div className="p-8 text-center text-gray-500 dark:text-graphite-muted text-sm">
        Žádné marketingové akce nebyly nalezeny.
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-graphite-border">
          <thead className="bg-gray-50 dark:bg-graphite-surface-2">
            <tr>
              {["Název", "Typ", "Od", "Do", "Produkty"].map((h) => (
                <th
                  key={h}
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-graphite-muted uppercase tracking-wider"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-graphite-surface divide-y divide-gray-100 dark:divide-graphite-border">
            {actions.map((action) => (
              <tr
                key={action.id}
                onClick={() => onActionClick(action.id!)}
                className="hover:bg-gray-50 dark:hover:bg-white/5 cursor-pointer transition-colors"
              >
                <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-graphite-text">
                  <div className="flex items-center gap-1.5">
                    {action.title}
                    {action.outlookSyncStatus === 'Failed' && (
                      <span
                        role="img"
                        aria-label="Synchronizace s Outlookem selhala – bude opakována"
                        title="Synchronizace s Outlookem selhala – bude opakována"
                        className="block w-2 h-2 rounded-full bg-red-500 flex-shrink-0"
                      />
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      ACTION_TYPE_BADGE[action.actionType as keyof typeof ACTION_TYPE_BADGE] ??
                      "bg-gray-100 dark:bg-graphite-surface-2 text-gray-800 dark:text-graphite-muted"
                    }`}
                  >
                    {ACTION_TYPE_LABELS[action.actionType as keyof typeof ACTION_TYPE_LABELS] ??
                      action.actionType}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-graphite-muted">
                  {formatDate(action.dateFrom as string)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-graphite-muted">
                  {formatDate(action.dateTo as string)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-graphite-muted">
                  {action.associatedProducts?.join(", ") || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 px-4 py-3 border-t border-gray-200 dark:border-graphite-border">
          <button
            onClick={() => onPageChange(pageNumber - 1)}
            disabled={pageNumber <= 1}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="h-4 w-4 text-gray-600 dark:text-graphite-muted" />
          </button>
          <span className="text-sm text-gray-600 dark:text-graphite-muted">
            {pageNumber} / {totalPages}
          </span>
          <button
            onClick={() => onPageChange(pageNumber + 1)}
            disabled={pageNumber >= totalPages}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="h-4 w-4 text-gray-600 dark:text-graphite-muted" />
          </button>
        </div>
      )}
    </div>
  );
};

export default MarketingActionGrid;
